/**
 * Bridge to the Genesys web play host (`/play/[gameId]`, `/play-in-editor`, etc.).
 * Game code can call these helpers from builds that only depend on `@gnsx/genesys.js`.
 */

/** `window.dispatchEvent(new CustomEvent(..., { detail: { visible } }))` */
export const GENESYS_PLAY_SHELL_SET_FULLSCREEN_BUTTON_VISIBILITY = 'genesys:set-play-fullscreen-button-visible';

/**
 * Host hint for {@link https://developer.mozilla.org/en-US/docs/Web/API/Screen/orientation Screen Orientation}.
 * Best-effort: many browsers only allow `lock()` after a user gesture and while fullscreen (or as installed PWA).
 */
export type GenesysPlayShellScreenOrientation = 'landscape' | 'portrait' | 'natural' | 'unlock';

export interface GenesysPlayShellVisibilityOptions {
  /** Applied after host fullscreen when possible; `natural` / `unlock` release a prior lock. */
  screenOrientation?: GenesysPlayShellScreenOrientation;
}

export interface GenesysPlayShell {
  setFullscreenButtonVisible(visible: boolean, options?: GenesysPlayShellVisibilityOptions): void;
  getFullscreenButtonVisible(): boolean;
  getScreenOrientationPreference(): GenesysPlayShellScreenOrientation | undefined;
}

/** @internal */
export function readGenesysPlayShellFromWindow(win: Window): GenesysPlayShell | undefined {
  return (win as Window & { genesysPlayShell?: GenesysPlayShell }).genesysPlayShell;
}

/**
 * Resolves the play host shell from this window (standalone / isolated WebContents)
 * or from `parent` / `top` when the game runs in an iframe that does not mirror the API on the child window.
 */
export function getGenesysPlayShell(): GenesysPlayShell | undefined {
  if (typeof window === 'undefined') return undefined;

  const own = readGenesysPlayShellFromWindow(window);
  if (own) return own;

  try {
    if (window.parent !== window) {
      const fromParent = readGenesysPlayShellFromWindow(window.parent);
      if (fromParent) return fromParent;
    }
  } catch {
    // Cross-origin: cannot read parent.
  }

  try {
    if (window.top !== null && window.top !== window) {
      const fromTop = readGenesysPlayShellFromWindow(window.top);
      if (fromTop) return fromTop;
    }
  } catch {
    // Cross-origin.
  }

  return undefined;
}

export function dispatchGenesysPlayShellFullscreenButtonVisibility(
  visible: boolean,
  options?: GenesysPlayShellVisibilityOptions
): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(GENESYS_PLAY_SHELL_SET_FULLSCREEN_BUTTON_VISIBILITY, {
      detail: { visible, ...options },
    })
  );
}

/** Alias for {@link GenesysPlayShell} (host registration uses this name in the editor). */
export type GenesysPlayShellApi = GenesysPlayShell;
